// Cookie-based storage adapter for the Supabase auth client.
//
// Why cookies instead of localStorage:
//   localStorage is origin-scoped, so a session on aldiwan.uk is invisible
//   to www.aldiwan.uk. A cookie with domain=.aldiwan.uk is shared by both.
//
// Why chunking:
//   Supabase JWTs + user metadata easily exceed the 4 KB per-cookie limit.
//   Values larger than CHUNK_SIZE are split across numbered sibling cookies
//   (e.g. diwan-auth.0, diwan-auth.1) and reassembled on read.
//
// Dev fallback:
//   domain=.aldiwan.uk is rejected by browsers on localhost, so we fall back
//   to localStorage in non-production environments.

const PROD_HOSTNAMES = new Set(['aldiwan.uk', 'www.aldiwan.uk']);
const IS_PROD = typeof window !== 'undefined' && PROD_HOSTNAMES.has(window.location.hostname);

const COOKIE_DOMAIN  = '.aldiwan.uk';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds
const CHUNK_SIZE     = 3000;                 // bytes; leaves room for name + attributes

function buildOptions(maxAge = COOKIE_MAX_AGE) {
  return `domain=${COOKIE_DOMAIN}; path=/; max-age=${maxAge}; SameSite=Lax; Secure`;
}

function parseCookies() {
  if (typeof document === 'undefined') return {};
  return Object.fromEntries(
    document.cookie.split('; ')
      .filter(Boolean)
      .map(pair => {
        const idx = pair.indexOf('=');
        return [pair.slice(0, idx), pair.slice(idx + 1)];
      })
  );
}

function getItem(key) {
  const cookies = parseCookies();
  if (cookies[`${key}.0`] !== undefined) {
    let value = '';
    let i = 0;
    while (cookies[`${key}.${i}`] !== undefined) {
      value += decodeURIComponent(cookies[`${key}.${i}`]);
      i++;
    }
    return value;
  }
  return cookies[key] !== undefined ? decodeURIComponent(cookies[key]) : null;
}

function removeItem(key) {
  const expire = buildOptions(0);
  document.cookie = `${key}=; ${expire}`;
  const cookies = parseCookies();
  let i = 0;
  while (cookies[`${key}.${i}`] !== undefined) {
    document.cookie = `${key}.${i}=; ${expire}`;
    i++;
  }
}

function setItem(key, value) {
  removeItem(key);
  const encoded = encodeURIComponent(value);
  if (encoded.length <= CHUNK_SIZE) {
    document.cookie = `${key}=${encoded}; ${buildOptions()}`;
  } else {
    let i = 0;
    for (let offset = 0; offset < encoded.length; offset += CHUNK_SIZE, i++) {
      document.cookie = `${key}.${i}=${encoded.slice(offset, offset + CHUNK_SIZE)}; ${buildOptions()}`;
    }
  }
}

// In local dev (localhost) fall back to localStorage so cookies still work.
export const cookieStorage = IS_PROD
  ? { getItem, setItem, removeItem }
  : localStorage;
